import { all, call, put, putResolve, select, takeEvery, delay, fork } from 'redux-saga/effects'
import { clearCookie, clearScenarioCookie, getCookie, parseScenario, scenarioBuilder, setCookie } from '../../scenarios'
import { addAnswer, addPrefilledAnswers, clearAnswers } from '../answers-slice'
import {
  Answers,
  Scenario,
  ScenarioStepModifiableElements,
  ScenarioType,
  StepId,
  Steps,
  Translatable,
} from '../interfaces'
import {
  finishScenario,
  initScenario,
  setIsScenarioLoading,
  restartScenario,
  setOriginalScenarioType,
  setScenario,
  setScenarioAdditionalInformation,
  mergeScenarioAdditionalInformation,
  normalizeScenarioAdditionalInformation,
  startScenario,
  updateScenario,
  setScenarioCookie,
  setScenarioScenarioType,
} from '../scenario-slice'
import { setReferrerUrl } from '../referrer-slice'
import { setRedirectionUrl } from '../redirection-url-slice'
import {
  selectAnswers,
  selectNextStepId,
  selectRedirectionUrl,
  selectReferrerUrl,
  selectScenario,
  selectScenarioAdditionalInformation,
  selectScenarioType,
  selectSearchText,
} from '../selectors'
import { RootState } from '../../app/state'
import { clearSteps, gotoStep, nextStep } from '../step-slice'
import { isEmpty } from 'lodash'
import {
  addUserToEmailSegment,
  createQuestionnaire,
  CreateQuestionnaireRequest,
  CreateQuestionnaireResponse,
  findAvailableDownloadSpeeds,
  getEmailFromAnswers,
  registerToNewsletter,
} from '../../connectivity/api'
import {
  appendDefinedQueryParams,
  getAdditionalQueryParameters,
  getQueryParams,
  getQueryParamsFromString,
} from '../../connectivity/request'
import { createToast } from '../../components/toast'
import { getProvinceFromQueryParams, getCurrentProviderFromQueryParams } from '../../connectivity/queryParams'
import {
  getCurrentProviderCookieName,
  findProviderNameWithSlug,
  findProviderSlugWithName,
  mapUnzipAdditionalInfoLead,
} from '../../scenarios/utils'
import i18next from 'i18next'
import * as Sentry from '@sentry/react'
import { refreshPhonesCondensedSync, refreshPhonesSync } from './mobile'
import { getUserLocation } from '../../utils/geoip'
import { Location } from '../../location'
import { getLocale } from '../../i18n/locale'
import { sendGA4Event } from '../../analytics/ga4'
// import { isMorningTime } from '../../utils/currentTime'
import { VALID_EMAIL_REGEX } from '../../utils/regex'
import { updateIdentityTraitAfterStep } from '../../analytics/tracker'
// import { isTheDomainAWhitelabel } from '../../whitelabels/whitelabel-domains'

export function* scenarioSaga() {
  yield takeEvery(initScenario.type, initializeScenario)
  yield takeEvery(restartScenario.type, reloadScenario)
  yield takeEvery(finishScenario.type, completeScenario)
}

function* initializeScenario() {
  yield call(fetchLocationAndSetCookies)
  const scenario: Scenario = yield call(constructScenario)
  yield put(startScenario(scenario))

  const clientInfoEmail = getCookie('emsng')
  if (scenario.type === ScenarioType.CLIENT_INFO_INTERNET && VALID_EMAIL_REGEX.test(clientInfoEmail)) {
    yield put(setIsScenarioLoading(true))
    yield put(finishScenario({ [Steps.EMAIL_ADDRESS]: [clientInfoEmail] }))
  }
}

function* fetchLocationAndSetCookies(): Generator<any, void, Location> {
  const country = getCookie('country')
  const city = getCookie('city')

  if (!country || !city) {
    const location: Location = yield call(getUserLocation)
    if (location) {
      if (location.country) {
        setCookie('country', location.country.toLowerCase())
      }
      // if (location.province) {
      //   setCookie('province', location.province.toLowerCase())
      // }
      if (location.city) {
        setCookie('city', location.city.toLowerCase())
      }
      // if (location.postalCode) {
      //   setCookie('geo-postal-code', location.postalCode.toLowerCase());
      // }
    }
  }
}

export const shouldInterceptWithCanadaVisitor = (
  country: string,
  visitorCookie: string,
  scenarioType: ScenarioType,
  scenarioId: string,
): boolean => {
  const isClientInfoScenario =
    scenarioType === ScenarioType.CLIENT_INFO_INTERNET || /^client-info-/i.test(scenarioId || '')

  return country !== '' && country !== 'canada' && visitorCookie === '' && !isClientInfoScenario
}

export function* constructScenario(): any {
  let scenario: Scenario = yield select(selectScenario)
  const codedScenarioInitialInformation = getQueryParams().cei as string

  let answers: Answers = {}
  const additionalInformation = {}

  if (!scenario) {
    // Reset the persisted scenario only during the initial bootstrap. In dev,
    // StrictMode can trigger this saga twice, and clearing here avoids wiping a
    // freshly written preassembled cookie on the second pass.
    if (codedScenarioInitialInformation) {
      setCookie('scenario', JSON.stringify({}))
    }

    answers = getCookie('scenario') ? JSON.parse(getCookie('scenario'))['answers'] : {}

    const builder = scenarioBuilder()
    let scenarioId = getQueryParams().scenarioId as string
    let referrerUrl = getQueryParams().referrer as string
    let scenarioType = getQueryParams().scenarioType as ScenarioType
    let utmSource = getQueryParams().utm_source as string
    const provider = getQueryParams().provider as string
    const locale = getQueryParams().locale as string
    const pageGoLink = getQueryParams().link as string
    const isProviderSng = getQueryParams().providersng !== undefined
    const country = getCookie('country')
    const cookieVisitor = getCookie('visitor')
    const source = getQueryParams().source as string

    if (pageGoLink) {
      const additionalQueryParameters = getAdditionalQueryParameters()
      const parameterI = additionalQueryParameters.i as string
      const parameterP = additionalQueryParameters.p as string
      const parameterL = additionalQueryParameters.l as string
      const parameterC = additionalQueryParameters.c as string
      const parameterF = additionalQueryParameters.f as string

      yield put(
        setRedirectionUrl(
          isProviderSng
            ? appendDefinedQueryParams(pageGoLink, {
                i: parameterI,
                p: parameterP,
                l: parameterL,
                c: parameterC,
                f: parameterF,
              })
            : (`${pageGoLink}&i=${parameterI}&p=${parameterP}&l=${encodeURIComponent(
                parameterL,
              )}&c=${parameterC}&f=${parameterF}&dyn=1` as string),
        ),
      )
    }

    if (!country) {
      const location = yield call(getUserLocation)
      if (location) {
        location.country && setCookie('country', location.country.toLowerCase())
        location.province && setCookie('province', location.province.toLowerCase())
        location.city && setCookie('city', location.city.toLowerCase())
        location.postalCode && setCookie('geo-postal-code', location.postalCode.toLowerCase())
      }
    }

    if (shouldInterceptWithCanadaVisitor(country, cookieVisitor, scenarioType, scenarioId)) {
      if (scenarioType) {
        yield put(setScenarioScenarioType(scenarioType))
      }
      scenarioType = ScenarioType.CANADA_VISITOR
      scenarioId = ''
      if (getCookie('scenario')) {
        yield put(setScenarioCookie(JSON.parse(getCookie('scenario'))))
      }

      setCookie('scenario', JSON.stringify({}))

      if (source && source === 'page-immigrant') {
        if (!answers) {
          answers = {}
        }
        answers[Steps.START_VISITOR] = ['migrating']
      }
    }

    if (getCookie('scenario')) {
      const scenarioIsPreAssembled = JSON.parse(getCookie('scenario')).isPreAssembled
      if (!scenarioIsPreAssembled) {
        setCookie('scenario', JSON.stringify({}))
      }
    }

    let scenarioInitialInformation: Record<string, any> | undefined = undefined

    // To solve the problem that some providers have a space in the url that planhub provided
    if (scenarioType) {
      // We need to change check availability to check-availability
      if (scenarioType !== ScenarioType.CHECK_AVAILABILITY) {
        scenarioType = scenarioType.replace(/ /, '-') as ScenarioType
      }
    }

    if (referrerUrl) {
      if (!utmSource) {
        utmSource = getQueryParamsFromString(referrerUrl).utm_source as string
      }
    }

    if (!scenarioId) {
      if (!scenarioType) {
        if (getCookie('scenario')) scenarioId = JSON.parse(getCookie('scenario')).id
      }
    }

    if (scenarioId) {
      builder.withScenarioId(scenarioId as string)
    }

    if (!scenarioType) {
      if (getCookie('scenario')) scenarioType = JSON.parse(getCookie('scenario')).type
    }

    if (!referrerUrl) {
      if (document.referrer && !document.referrer.match(/canada-visitor/i)) {
        referrerUrl = document.referrer
        if (referrerUrl.match(/localhost/i)) {
          referrerUrl += 'app_dev.php'
        }
      } else {
        referrerUrl = 'https://www.planhub.ca'
      }
    }

    if (scenarioType === ScenarioType.MOBILE) {
      if (utmSource && (utmSource.toLowerCase() === 'hs' || utmSource.toLowerCase() === 'ipc')) {
        scenarioType = ScenarioType.MOBILE_SEARCH_EMAIL
      }
    }

    if (scenarioType === ScenarioType.ADVISE_INTERNET) {
      const listAdviseInternetProviders = ['fizz']

      if (provider && listAdviseInternetProviders.includes(provider)) {
        yield put(
          setScenarioAdditionalInformation({
            ...additionalInformation,
            downloadSpeeds: yield call(findAvailableDownloadSpeeds, provider),
            internetEstimationProvider: provider,
          }),
        )
      } else {
        scenarioType = ScenarioType.INTERNET
      }
    }

    const scenariosTypeAlert = [
      ScenarioType.MOBILE_ALERT,
      ScenarioType.INTERNET_ALERT,
      ScenarioType.MOBILE_ALERT_OBSERVER,
      ScenarioType.INTERNET_ALERT_OBSERVER,
    ]

    if (scenariosTypeAlert.includes(scenarioType)) {
      const decision: string = getQueryParams().decision as string
      const emailFromUrl = getQueryParams().emailofuser as string
      const consent = getQueryParams().consent as string

      // Use the same regex as in the email_address input component
      if (emailFromUrl && VALID_EMAIL_REGEX.test(emailFromUrl)) {
        yield fork(addUserToEmailSegment, emailFromUrl)

        if (consent) {
          yield put(
            setScenarioAdditionalInformation({
              ...additionalInformation,
              emailofuser: emailFromUrl,
              consent: consent,
            }),
          )

          if (consent === '1') {
            yield put(
              addPrefilledAnswers({
                [Steps.CONFIRM_ALERT]: ['confirm-alert'],
              }),
            )
          } else if (consent === '2') {
            yield fork(registerToNewsletter, emailFromUrl)
            yield put(
              addPrefilledAnswers({
                [Steps.CONFIRM_ALERT]: ['confirm-alert', 'confirm-newsletters'],
              }),
            )
          }
        } else {
          yield put(
            setScenarioAdditionalInformation({
              ...additionalInformation,
              emailofuser: emailFromUrl,
            }),
          )
        }
      }

      if (!decision) {
        yield put(setOriginalScenarioType(scenarioType))
        scenarioType = ScenarioType.DECISION_ALERT
      }
    }

    if (scenarioType) {
      builder.withScenarioType(scenarioType)
    }

    if (getCookie('scenario') && JSON.stringify(getCookie('scenario')) !== '{}') {
      scenarioInitialInformation = JSON.parse(getCookie('scenario')).initialInformation
    }

    if (!scenarioInitialInformation) {
      if (codedScenarioInitialInformation) {
        try {
          scenarioInitialInformation = JSON.parse(atob(codedScenarioInitialInformation))
          // reconvertir les clés en camelCase
          const restoredScenarioInitialInformation = restoreScenarioInitialInformation(scenarioInitialInformation)
          scenarioInitialInformation = restoredScenarioInitialInformation
          const scenarioInfo = {
            isPreAssembled: true,
            type: scenarioType || 'lead-mobile',
            locale: locale,
            answers: {},
            initialInformation: scenarioInitialInformation,
          }
          setCookie('scenario', JSON.stringify(scenarioInfo))
          if (scenarioInitialInformation.providerName) {
            setCookie('providerName', scenarioInitialInformation.providerName)
          }
          yield put(
            setScenarioAdditionalInformation({
              ...scenarioInitialInformation,
            }),
          )
          yield put(setScenarioCookie(scenarioInfo))
        } catch (e) {
          console.warn('Error parsing scenarioInitialInformation')
        }
      }
    }

    yield put(setReferrerUrl(referrerUrl as string))

    if (utmSource && utmSource === 'finder') {
      yield put(
        setScenarioAdditionalInformation({
          ...additionalInformation,
          utmSource: utmSource,
        }),
      )
    }

    const isClientInfoScenario =
      scenarioType === ScenarioType.CLIENT_INFO_INTERNET || /^client-info-/i.test(scenarioId || '')

    if (isClientInfoScenario) {
      yield put(
        mergeScenarioAdditionalInformation({
          clientInfoAffiliation: (getQueryParams().wa as string) || '',
          clientInfoTrackedUrl: pageGoLink || '',
        }),
      )
    }

    scenario = builder.build()
    const { initialStep } = scenario

    if (scenarioInitialInformation) {
      scenario.initialInformation = scenarioInitialInformation

      if (scenarioType === ScenarioType.QUICK_SEARCH_MOBILE) {
        if (scenarioInitialInformation?.variant) {
          yield put(
            setScenarioAdditionalInformation({
              ...additionalInformation,
              providerIdQuickSearch: scenarioInitialInformation.providerIdQuickSearch,
              variant: scenarioInitialInformation.variant,
            }),
          )
        }
      }
    }

    yield put(setScenario(scenario))
    yield put(gotoStep({ id: initialStep }))

    if (!isEmpty(answers)) {
      yield putResolve(addPrefilledAnswers(answers))
    }
  } else {
    answers = getCookie('scenario') ? JSON.parse(getCookie('scenario'))['answers'] : {}
  }

  if (answers && answers['email-address'] && answers['email-address'].length > 0) {
    yield call(updateIdentityTraitAfterStep, 'email-address' as StepId, [answers['email-address'][0]])
  }

  const isProvinceFromQuestionnaire = getCookie('stepQ') || getCookie('forcePhoneListUpdate')

  const province =
    getProvinceFromQueryParams() ||
    getCookie(Steps.PROVINCE) ||
    (answers && answers[Steps.PROVINCE] ? answers[Steps.PROVINCE]![0] : undefined)

  const emailOfUser = getQueryParams().emailofuser as string

  const currentProviderCookieName: string = getCurrentProviderCookieName(scenario.type)

  const currentProvider =
    getCurrentProviderFromQueryParams() ||
    getCookie(currentProviderCookieName) ||
    (answers && answers[Steps.CURRENT_PROVIDER] ? answers[Steps.CURRENT_PROVIDER]![0] : undefined)

  const shouldRefreshPhones = (scenario: Scenario): boolean => {
    return (
      scenario.type === ScenarioType.MOBILE || // && !isMorningTime()) ||
      scenario.type === ScenarioType.MOBILE_SEARCH_EMAIL ||
      scenario.type === ScenarioType.MOBILE_ALERT ||
      scenario.type === ScenarioType.QUICK_SEARCH_MOBILE ||
      scenario.type === ScenarioType.PHONE_SEARCH
    )
  }

  const isQuickSearchByod = (scenario: Scenario, scenarioAdditionalInformation: Record<string, unknown>): boolean => {
    return scenario.type === ScenarioType.QUICK_SEARCH_MOBILE && scenarioAdditionalInformation?.variant === 'byod'
  }

  const isCanadianProvince = (province: string): boolean => {
    const validProvinces = [
      'quebec',
      'ontario',
      'saskatchewan',
      'alberta',
      'manitoba',
      'new-brunswick',
      'nova-scotia',
      'prince-edward',
      'newfoundland-labrador',
      'yukon',
      'british-columbia',
      'northwest-territories',
      'nunavut',
    ]

    return validProvinces.includes(province.toLowerCase())
  }

  if (province && isProvinceFromQuestionnaire && isCanadianProvince(province)) {
    setCookie(Steps.PROVINCE, province)
    yield put(
      addPrefilledAnswers({
        [Steps.PROVINCE]: [province],
      }),
    )

    if (shouldRefreshPhones(scenario)) {
      const scenarioAdditionalInformation: Record<string, unknown> = yield select(selectScenarioAdditionalInformation)
      if (!isQuickSearchByod(scenario, scenarioAdditionalInformation)) {
        yield call(refreshPhonesSync)
      }
    }

    if (getCookie('forcePhoneListUpdate')) {
      setCookie('forcePhoneListUpdate', 'false')
    }
  }

  if (scenario.type === ScenarioType.PHONE_SEARCH) {
    yield call(refreshPhonesSync) // temporary fix
    yield call(refreshPhonesCondensedSync)
  }

  if (emailOfUser && VALID_EMAIL_REGEX.test(emailOfUser)) {
    // By adding put(adAnswer(..., prefilled:true)) we are able to prefill the email and call the event adAnswer
    // instead of doing

    // yield put(
    //   addPrefilledAnswers({
    //     [Steps.EMAIL_ADDRESS]: [emailOfUser],
    //   }),
    // )

    yield put(
      addAnswer({
        stepId: Steps.EMAIL_ADDRESS,
        answerIds: [emailOfUser],
        hidden: false,
        prefilled: true,
      }),
    )
  }

  if (scenario.type === ScenarioType.MOBILE) {
    /* This must be deprecated in the future */
    const bigEntreprise = getCookie(Steps.BIG_ENTREPRISE)

    // I want this question to be shown to client only if the province is quebec
    if (province && province !== 'quebec') {
      yield put(
        addPrefilledAnswers({
          [Steps.BIG_ENTREPRISE2]: ['not-set'],
        }),
      )
    }

    if (bigEntreprise) {
      // yield put(
      //   addPrefilledAnswers({
      //     [Steps.BIG_ENTREPRISE]: [bigEntreprise],
      //   }),
      // )

      yield put(
        addPrefilledAnswers({
          [Steps.BIG_ENTREPRISE2]: [bigEntreprise],
        }),
      )
    }
  }

  /* Deprecated until this line (see before) */

  // yield put(
  //   addPrefilledAnswers({
  //     [Steps.PROVIDER_AVAILABILITY]: ['continue-with-provider', 'true'],
  //   }),
  // )

  const providerSlug = getQueryParams().providerSlug as string
  let providerName = getQueryParams().providerName as string

  if (!providerName) {
    providerName = getQueryParams().provider as string
  }

  clearCookie('providerSlugReviews')
  clearCookie('providerNameReviews')

  if (providerSlug) {
    yield put(
      addPrefilledAnswers({
        [Steps.SELECT_CURRENT_PROVIDER]: [providerSlug],
      }),
    )
    findProviderNameWithSlug(providerSlug, scenario)
  } else {
    if (providerName) {
      yield findProviderSlugWithName(providerName, scenario)
      yield put(
        addPrefilledAnswers({
          [Steps.SELECT_CURRENT_PROVIDER]: [getCookie('providerSlug')],
        }),
      )
    }
  }

  if (currentProvider) {
    setCookie(currentProviderCookieName, currentProvider)
    yield put(
      addPrefilledAnswers({
        [Steps.CURRENT_PROVIDER]: [currentProvider],
      }),
    )
  }

  if (scenario.type === ScenarioType.PHONE_SEARCH) {
    const currentProviderMobile: string = getCookie('currentMobileProvider')
    if (currentProviderMobile) {
      yield put(
        addPrefilledAnswers({
          [Steps.CURRENT_PROVIDER_MOBILE_PHONE_SEARCH]: [currentProviderMobile],
        }),
      )
    }
  }

  if (scenario.type === ScenarioType.INTERNET) {
    const currentProviderMobile: string = getCookie('currentMobileProvider')
    if (currentProviderMobile) {
      yield put(
        addPrefilledAnswers({
          [Steps.CURRENT_PROVIDER_MOBILE]: [currentProviderMobile],
        }),
      )
    }
  }

  if (scenario.type === ScenarioType.MOBILE) {
    const currentProviderInternet: string = getCookie('currentInternetProvider')
    if (currentProviderInternet) {
      yield put(
        addPrefilledAnswers({
          [Steps.CURRENT_PROVIDER_INTERNET]: [currentProviderInternet],
        }),
      )
    }
  }

  // To allow the client to change the provider in the questionnaire
  // if (scenario.type === ScenarioType.LEAD_INTERNET || scenario.type === ScenarioType.LEAD_MOBILE) {
  //   const currentProviderMobile: string = getCookie('currentMobileProvider')
  //   if (currentProviderMobile) {
  //     yield put(
  //       addPrefilledAnswers({
  //         [Steps.CURRENT_PROVIDER_MOBILE_LEAD]: [currentProviderMobile],
  //       }),
  //     )
  //   }
  // }

  // Commmented out because we don't want to show this question anymore
  // if (scenario.type === ScenarioType.INTERNET) {
  //   if (!isMorningTime() || isTheDomainAWhitelabel(window.location.hostname)) {
  //     yield put(
  //       addPrefilledAnswers({
  //         [Steps.BUSINESS_OR_NOT_INTERNET]: ['hide'],
  //       }),
  //     )
  //   }
  // }

  // if (scenario.type === ScenarioType.MOBILE) {
  //   if (!isMorningTime() || isTheDomainAWhitelabel(window.location.hostname)) {
  //     yield put(
  //       addPrefilledAnswers({
  //         [Steps.BUSINESS_OR_NOT_MOBILE]: ['hide'],
  //       }),
  //     )
  //   }
  // }

  // if (!scenario?.initialInformation?.hasPrefilledAnswers) {
  //   if (scenario.type === ScenarioType.MOBILE_ALERT) {
  //     yield put(
  //       addPrefilledAnswers({
  //         [Steps.START_MOBILE_ALERT]: ['start'],
  //       }),
  //     )
  //   } else if (scenario.type === ScenarioType.INTERNET_ALERT) {
  //     yield put(
  //       addPrefilledAnswers({
  //         [Steps.START_INTERNET_ALERT]: ['start'],
  //       }),
  //     )
  //   }
  // }

  const providedAnswers: Answers = yield select(selectAnswers)

  if (providedAnswers[scenario.initialStep]) {
    let nextStepId: StepId | null = yield select(selectNextStepId)
    if (nextStepId) {
      yield putResolve(nextStep({ id: nextStepId }))

      while (nextStepId && providedAnswers[nextStepId]) {
        nextStepId = yield select(selectNextStepId)
        if (!nextStepId) break
        yield putResolve(nextStep({ id: nextStepId }))
      }
    }
  }

  const state: RootState = yield select()
  scenario = yield call(parseScenario, { scenario, state })

  if (scenario?.initialStep === Steps.EMAIL_ADDRESS_LEAD) {
    const providerName: string | undefined = scenario.initialInformation?.providerName

    if (providerName) {
      setCookie('providerName', providerName as string)

      if (providerName?.toLowerCase() === 'bell') {
        scenario.steps[0].tip = i18next.t('email-address-lead_tip', {
          providerName,
        }) as any
      } else {
        scenario.steps[0].tip = i18next.t('email-address-no-bell-lead_tip', {
          providerName,
        }) as any
      }
    }
  }

  if (scenario?.initialStep === Steps.TICKET_INITIAL_INFO) {
    const providerName: string = scenario.initialInformation!.providerName
    // const variant: string = scenario.initialInformation!.variant
    const initialInfoStep = scenario.steps.find((step) => step.id === Steps.TICKET_INITIAL_INFO)

    if (initialInfoStep) {
      if (providerName) {
        const answers = initialInfoStep!.answers.map((answer) => {
          if (answer.id === 'cant-find-offer') {
            answer.text = (answer.text as string).replace(/provider/i, providerName) as Translatable
            return answer
          }
          return answer
        })

        yield put(
          updateScenario({
            stepToUpdate: Steps.TICKET_INITIAL_INFO,
            value: answers,
            element: ScenarioStepModifiableElements.ANSWERS,
          }),
        )
      }
    }
  }

  if (scenario.type === ScenarioType.EMAIL_PAGE_GO_INTERNET || scenario.type === ScenarioType.EMAIL_PAGE_GO_MOBILE) {
    window.addEventListener('beforeunload', () => {
      sendGA4Event('GA4_email_skip', {
        postal_code: getCookie('internetSearchPostalcode') || getCookie('geo-postal-code') || 'not set',
        region: (answers && answers['province']?.[0]) || getCookie('province') || 'not set',
        locale: getLocale(),
        provider_name: getCookie('providerName') || 'not set',
        misc: 'closetab',
      })
    })
  }

  return scenario
}

export function restoreScenarioInitialInformation(
  scenarioInitialInformation: Record<string, any> | undefined,
): Record<string, any> {
  const restoredScenarioInitialInformation: Record<string, any> = {}
  const mapUnzipAdditionalInfo: Record<string, any> = mapUnzipAdditionalInfoLead()

  if (scenarioInitialInformation) {
    for (const key in scenarioInitialInformation) {
      if (key in mapUnzipAdditionalInfo) {
        restoredScenarioInitialInformation[mapUnzipAdditionalInfo[key]] = scenarioInitialInformation[key]
      }
    }
  }

  return normalizeScenarioAdditionalInformation(restoredScenarioInitialInformation)
}

function* reloadScenario() {
  const scenario: Scenario = yield select(selectScenario)

  yield all([put(gotoStep({ id: scenario?.initialStep })), put(clearSteps()), put(clearAnswers())])
}

function* completeScenario({ payload }: ReturnType<typeof finishScenario>) {
  const answers: Answers = yield select(selectAnswers)
  const scenarioType: string = yield select(selectScenarioType)
  const scenarioAdditionalInformation: Record<string, unknown> = yield select(selectScenarioAdditionalInformation)
  let referrer: string = yield select(selectReferrerUrl)
  const searchText: string = yield select(selectSearchText)
  const redirectionUrl: string = yield select(selectRedirectionUrl)
  const email = getEmailFromAnswers(answers)

  if (email && email !== 'skipped') {
    yield delay(3000)
  }

  clearScenarioCookie()
  if (!referrer) {
    referrer = document.referrer || 'https://www.planhub.ca'
  }

  const request: CreateQuestionnaireRequest = {
    referrerUrl: (getQueryParams().referrer as string) || referrer || '',
    redirectionUrl: redirectionUrl || '',
    scenarioType: scenarioType,
    answers: {
      ...answers,
      ...payload,
    },
    scenarioAdditionalInformation,
    searchText,
  }

  try {
    const response: CreateQuestionnaireResponse = yield call(createQuestionnaire, request)
    if (response.redirectUrl) {
      if (scenarioType === ScenarioType.EMAIL_PAGE_GO_INTERNET || scenarioType === ScenarioType.EMAIL_PAGE_GO_MOBILE) {
        window.location.replace(response.redirectUrl)
      } else {
        window.location.assign(response.redirectUrl)
      }
    } else {
      window.location.assign(request.referrerUrl)
    }
  } catch (error: any) {
    createToast(i18next.t('generic-api_error'), error)
    Sentry.captureMessage(error.toString())
  }
}
